/**
 * WordPress dependencies
 */
import { __ } from '@wordpress/i18n';
import {
	useBlockProps,
	InspectorControls,
	RichText,
	PanelColorSettings,
	MediaUpload,
	MediaUploadCheck,
	MediaReplaceFlow,
	BlockControls,
} from '@wordpress/block-editor';
import {
	PanelBody,
	SelectControl,
	ToggleControl,
	RangeControl,
	Placeholder,
	Button,
	Spinner,
} from '@wordpress/components';
import { useState } from '@wordpress/element';

/**
 * The edit function for the Before & After Photos block.
 *
 * @param {Object}   props               Properties passed to the function.
 * @param {Object}   props.attributes    Available block attributes.
 * @param {Function} props.setAttributes Function that updates individual attributes.
 *
 * @return {WP_Element} Element to render.
 */
export default function Edit({ attributes, setAttributes }) {
	const {
		heading,
		subheading,
		gallery,
		ids,
		imageCount,
		layout,
		aspectRatio,
		showArrows,
		showDots,
		showCaptions,
		autoplay,
		autoplaySpeed,
		enableLightbox,
		rounded,
		backgroundColor,
		textColor,
		customBackgroundColor,
		customTextColor,
	} = attributes;

	const [isUploading, setIsUploading] = useState(false);

	const blockProps = useBlockProps({
		className: 'wp-block-puroclean-before-after alignfull max-w-none',
		style: {
			backgroundColor: customBackgroundColor || undefined,
			color: customTextColor || undefined,
		},
	});

	// Handle gallery selection - better UX with ids
	const onSelectGallery = (selectedImages) => {
		const newIds = selectedImages.map(img => img.id);
		setAttributes({ 
			gallery: selectedImages,
			ids: newIds
		});
	};

	// Handle adding more images to existing gallery
	const onAddImages = (newImages) => {
		const updatedGallery = [...gallery, ...newImages];
		const updatedIds = updatedGallery.map(img => img.id);
		setAttributes({ 
			gallery: updatedGallery,
			ids: updatedIds
		});
	};

	// Move image in gallery
	const moveImage = (fromIndex, toIndex) => {
		const newGallery = [...gallery];
		const [movedImage] = newGallery.splice(fromIndex, 1);
		newGallery.splice(toIndex, 0, movedImage);
		setAttributes({ gallery: newGallery });
	};

	// Remove image from gallery
	const removeImage = (imageId) => {
		const newGallery = gallery.filter(img => img.id !== imageId);
		setAttributes({ gallery: newGallery });
	};

	// Get rounded class based on rounded attribute
	const getRoundedClass = (roundedValue) => {
		switch (roundedValue) {
			case 's':
				return 'rounded';
			case 'm':
				return 'rounded-md';
			case 'l':
				return 'rounded-lg';
			case 'xl':
				return 'rounded-xl';
			case '2xl':
				return 'rounded-2xl';
			case 'none':
			default:
				return 'rounded-none';
		}
	};

	// Get limited gallery for display - 0 means show all images
	const displayGallery = imageCount > 0 ? gallery.slice(0, imageCount) : gallery;

	return (
		<>
			<InspectorControls>
				{/* Layout Settings */}
				<PanelBody title={__('Layout Settings', 'puroclean')} initialOpen={false}>
					<SelectControl
						label={__('Layout', 'puroclean')}
						value={layout}
						options={[
							{ label: __('Carousel', 'puroclean'), value: 'carousel' },
							{ label: __('Grid', 'puroclean'), value: 'grid' },
						]}
						onChange={(value) => setAttributes({ layout: value })}
					/>
					<SelectControl
						label={__('Aspect Ratio', 'puroclean')}
						value={aspectRatio}
						options={[
							{ label: __('16:9 (Widescreen)', 'puroclean'), value: '16/9' },
							{ label: __('4:3 (Standard)', 'puroclean'), value: '4/3' },
							{ label: __('1:1 (Square)', 'puroclean'), value: '1/1' },
							{ label: __('Auto (Original)', 'puroclean'), value: 'auto' },
						]}
						onChange={(value) => setAttributes({ aspectRatio: value })}
					/>
					<SelectControl
						label={__('Border Radius', 'puroclean')}
						value={rounded}
						options={[
							{ label: __('None', 'puroclean'), value: 'none' },
							{ label: __('Small (0.25rem)', 'puroclean'), value: 's' },
							{ label: __('Medium (0.375rem)', 'puroclean'), value: 'm' },
							{ label: __('Large (0.5rem)', 'puroclean'), value: 'l' },
							{ label: __('Extra Large (0.75rem)', 'puroclean'), value: 'xl' },
							{ label: __('2X Large (1rem)', 'puroclean'), value: '2xl' },
						]}
						onChange={(value) => setAttributes({ rounded: value })}
						help={__('Add rounded corners to the images in the slider', 'puroclean')}
					/>
				</PanelBody>

				{/* Display Settings */}
				<PanelBody title={__('Display Settings', 'puroclean')} initialOpen={false}>
					<RangeControl
						label={__('Number of Images to Display', 'puroclean')}
						value={imageCount}
						onChange={(value) => setAttributes({ imageCount: value })}
						min={0}
						max={50}
						step={1}
						help={__('Set to 0 to display all images (unlimited)', 'puroclean')}
					/>
					<ToggleControl
						label={__('Show Navigation Arrows', 'puroclean')}
						checked={showArrows}
						onChange={(value) => setAttributes({ showArrows: value })}
					/>
					<ToggleControl
						label={__('Show Navigation Dots', 'puroclean')}
						checked={showDots}
						onChange={(value) => setAttributes({ showDots: value })}
					/>
					<ToggleControl
						label={__('Show Image Captions', 'puroclean')}
						checked={showCaptions}
						onChange={(value) => setAttributes({ showCaptions: value })}
					/>
					<ToggleControl
						label={__('Enable Lightbox', 'puroclean')}
						checked={enableLightbox}
						onChange={(value) => setAttributes({ enableLightbox: value })}
						help={__('Allow users to click images to view them in a lightbox overlay', 'puroclean')}
					/>
				</PanelBody>

				{/* Autoplay Settings */}
				<PanelBody title={__('Autoplay Settings', 'puroclean')} initialOpen={false}>
					<ToggleControl
						label={__('Enable Autoplay', 'puroclean')}
						checked={autoplay}
						onChange={(value) => setAttributes({ autoplay: value })}
					/>
					{autoplay && (
						<RangeControl
							label={__('Autoplay Speed (ms)', 'puroclean')}
							value={autoplaySpeed}
							onChange={(value) => setAttributes({ autoplaySpeed: value })}
							min={1000}
							max={10000}
							step={500}
						/>
					)}
				</PanelBody>
			</InspectorControls>

			<div {...blockProps}>
				<div className="bg-secondary mx-auto px-4 py-12 lg:py-16">
					{/* Header Section - Editable in Editor */}
					<div className="text-center mb-8 lg:mb-12">
						<RichText
							tagName="h2"
							className="text-3xl lg:text-4xl xl:text-5xl font-bold !text-gray-50 mb-4"
							value={heading}
							onChange={(value) => setAttributes({ heading: value })}
							placeholder={__('Enter heading...', 'puroclean')}
							allowedFormats={['core/bold', 'core/italic']}
						/>
						
						{(subheading || subheading === '') && (
							<RichText
								tagName="p"
								className="text-lg lg:text-xl !text-gray-100 max-w-3xl mx-auto"
								value={subheading}
								onChange={(value) => setAttributes({ subheading: value })}
								placeholder={__('Enter optional subheading...', 'puroclean')}
								allowedFormats={['core/bold', 'core/italic']}
							/>
						)}
					</div>

					{/* Gallery Management Section */}
					<div className="max-w-6xl mx-auto">
						<div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
							<div className="flex items-center justify-between mb-6">
								<h3 className="text-lg font-semibold text-black">
									{__('Before & After Gallery', 'puroclean')}
								</h3>
								<MediaUploadCheck>
									<div className="flex gap-2">
										{/* Add Images Button */}
										<MediaUpload
											onSelect={gallery.length === 0 ? onSelectGallery : onAddImages}
											allowedTypes={['image']}
											multiple={true}
											render={({ open }) => (
												<Button
													variant="primary"
													onClick={open}
													className="bg-blue-600 hover:bg-blue-700 !text-white"
													disabled={isUploading}
												>
													{isUploading ? (
														<>
															<Spinner />
															{__('Uploading...', 'puroclean')}
														</>
													) : (
														gallery.length === 0 ? __('Add Images', 'puroclean') : __('Add More Images', 'puroclean')
													)}
												</Button>
											)}
										/>
										
										{/* Edit Gallery Button - only show when gallery has images */}
										{gallery.length > 0 && (
											<MediaUpload
												onSelect={onSelectGallery}
												allowedTypes={['image']}
												multiple={true}
												value={ids}
												render={({ open }) => (
													<Button
														variant="secondary"
														onClick={open}
														className="bg-gray-600 hover:bg-gray-700 !text-white"
													>
														{__('Edit Gallery', 'puroclean')}
													</Button>
												)}
											/>
										)}
									</div>
								</MediaUploadCheck>
							</div>
							
							{gallery.length > 0 ? (
								<div className="space-y-4">
									<div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
										{gallery.map((image, index) => (
											<div 
												key={image.id} 
												className="relative group bg-white/5 rounded-lg p-2"
												draggable
												onDragStart={(e) => {
													e.dataTransfer.setData('text/plain', index);
													e.dataTransfer.effectAllowed = 'move';
												}}
												onDragOver={(e) => e.preventDefault()}
												onDrop={(e) => {
													e.preventDefault();
													const fromIndex = parseInt(e.dataTransfer.getData('text/plain'));
													const toIndex = index;
													if (fromIndex !== toIndex) {
														moveImage(fromIndex, toIndex);
													}
												}}
											>
												<img
													src={image.url || image.sizes?.medium?.url || image.sizes?.thumbnail?.url}
													alt={image.alt || image.title || ''}
													className={`w-full h-24 object-cover ${getRoundedClass(rounded)}`}
												/>
												<div className={`absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity ${getRoundedClass(rounded)} flex items-center justify-center gap-2`}>
													<Button
														onClick={() => moveImage(index, Math.max(0, index - 1))}
														disabled={index === 0}
														className="bg-white/20 hover:bg-white/30 text-white p-1"
														aria-label={__('Move left', 'puroclean')}
													>
														←
													</Button>
													<Button
														onClick={() => removeImage(image.id)}
														className="bg-red-600 hover:bg-red-700 text-white p-1"
														aria-label={__('Remove image', 'puroclean')}
													>
														×
													</Button>
													<Button
														onClick={() => moveImage(index, Math.min(gallery.length - 1, index + 1))}
														disabled={index === gallery.length - 1}
														className="bg-white/20 hover:bg-white/30 text-white p-1"
														aria-label={__('Move right', 'puroclean')}
													>
														→
													</Button>
												</div>
												<div className="mt-2 text-xs text-gray-600 truncate">
													{image.caption || image.title || __('Untitled', 'puroclean')}
												</div>
												<div className="text-xs text-gray-500">
													{__('Position', 'puroclean')}: {index + 1}
												</div>
											</div>
										))}
									</div>
									
									<div className="text-sm text-gray-600 bg-gray-100 rounded-lg p-3">
										<p><strong className="text-gray-800">{__('Total images', 'puroclean')}:</strong> {gallery.length}</p>
										<p><strong className="text-gray-800">{__('Display limit', 'puroclean')}:</strong> {imageCount === 0 ? __('All images', 'puroclean') : imageCount}</p>
										<p><strong className="text-gray-800">{__('Layout', 'puroclean')}:</strong> {layout}</p>
									</div>
								</div>
							) : (
								<div className="text-center py-8 text-gray-500">
									<svg className="w-12 h-12 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
									</svg>
									<p>{__('No images selected. Click "Add/Edit Images" to get started.', 'puroclean')}</p>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</>
	);
}